type FrameTimer = {
  cancel: () => void;
  isScheduled: () => boolean;
  hasTimeout: () => boolean;
  scheduleRaf: (run: (nowMs: number) => void) => void;
  scheduleTimeout: (run: (nowMs: number) => void, delayMs: number) => void;
};

function createFrameTimer(): FrameTimer {
  let frameId = 0;
  let timeoutId = 0;
  let scheduled = false;

  return {
    cancel: () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      if (timeoutId) window.clearTimeout(timeoutId);
      frameId = 0;
      timeoutId = 0;
      scheduled = false;
    },
    isScheduled: () => scheduled,
    hasTimeout: () => timeoutId !== 0,
    scheduleRaf: (run) => {
      scheduled = true;
      frameId = window.requestAnimationFrame(run);
    },
    scheduleTimeout: (run, delayMs) => {
      scheduled = true;
      timeoutId = window.setTimeout(() => run(performance.now()), delayMs);
    },
  };
}

export type BoardFrameLoop = {
  requestFrame: () => void;
  cancel: () => void;
  dispose: () => void;
};

export function createBoardFrameLoop({
  getDelayMs,
  paint,
}: {
  getDelayMs: (nowMs: number) => number | null;
  paint: (nowMs: number) => void;
}): BoardFrameLoop {
  const timer = createFrameTimer();

  const runFrame = (nowMs: number): void => {
    timer.cancel();
    paint(nowMs);
    if (document.hidden) return;
    const delay = getDelayMs(nowMs);
    if (delay === null) return;
    if (delay === 0) {
      timer.scheduleRaf(runFrame);
      return;
    }
    timer.scheduleTimeout(runFrame, delay);
  };

  const requestFrame = (): void => {
    if (document.hidden) return;
    if (timer.isScheduled() && !timer.hasTimeout()) return;
    timer.cancel();
    timer.scheduleRaf(runFrame);
  };

  const onVisibilityChange = (): void => {
    if (document.hidden) {
      timer.cancel();
      return;
    }
    requestFrame();
  };

  document.addEventListener("visibilitychange", onVisibilityChange);

  return {
    requestFrame,
    cancel: () => timer.cancel(),
    dispose: () => {
      timer.cancel();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    },
  };
}
